// Flux Standard Actions
// https://github.com/acdlite/flux-standard-action
// ----------------------------------------------------------------------------

import zipObject from 'lodash/zipObject'
import {AnyAction, Dispatch} from 'redux'
import {RootState} from '../store/types'

// for nullary actions
export const fsa =
  <T>(type: T) =>
  (): {type: T} => ({type})

// for unary actions

export const fsaPayload =
  <T>(type: T) =>
  <K>(payload: K): {payload: K; type: T} => ({
    payload,
    type,
  })

// for plenary actions
// imagine:
//
//   fsaPayloadByKeys(UPDATE, ['email', 'name'])
//
// is
//
//   (email, name) => ({payload: {email, name}, type: UPDATE})
//

interface ZippedPayload {
  [key: string]: unknown
}

export const fsaPayloadByKeys =
  <T>(type: T, keys: string[]) =>
  (...args: unknown[]): {payload: ZippedPayload; type: T} => ({
    payload: zipObject(keys, args),
    type,
  })

// for unary actions classified as errors
// `payload` should be type Error

export const fsaError =
  <T>(type: T) =>
  <K>(payload: K): {error: boolean; payload: K; type: T} => ({
    error: true,
    payload,
    type,
  })

// any actions with variable arity or arity > 1 should be written by hand,
//  unless a worthwhile abstraction can be devised
type boolFuncType = (x: boolean) => boolean
const bool: boolFuncType = (x) => !!x

type gateActionType = (
  action: () => AnyAction,
  selector: (state: RootState) => RootState, // This should be a fn that returns STATE
  valid?: boolFuncType,
) => () => (dispatch: Dispatch, getState: RootState) => void

export const gateAction: gateActionType =
  (action, selector, valid = bool) =>
  () =>
  (dispatch, getState) => {
    if (valid(selector(getState()))) dispatch(action())
  }
